import { useMemo, useState } from 'react';
import Card from '../ui/Card';
import Avatar from '../ui/Avatar';
import { Skeleton } from '../Skeleton';
import { MODULE_LABELS, type Submission } from '../../mentorshipService';

const SLA_DAYS = 7;
const SLA_WARN_HOURS = 48;

type Props = {
    submissions: Submission[] | null;
    loading: boolean;
    activeId: string | null;
    onSelect: (s: Submission) => void;
};

function slaChip(submittedAt: string): { label: string; cls: string } {
    const submitted = new Date(submittedAt).getTime();
    const dueAt = submitted + SLA_DAYS * 24 * 3600 * 1000;
    const remainingMs = dueAt - Date.now();
    if (remainingMs <= 0) {
        return { label: 'Overdue', cls: 'mentorship-chip--sla-breach' };
    }
    const remainingHours = remainingMs / 3600 / 1000;
    if (remainingHours <= SLA_WARN_HOURS) {
        const h = Math.round(remainingHours);
        return { label: `${h}h left`, cls: 'mentorship-chip--sla-warn' };
    }
    const days = Math.ceil(remainingHours / 24);
    return { label: `${days}d left`, cls: 'mentorship-chip--sla-ok' };
}

function menteeName(s: Submission): string {
    const m = s.mentee_summary;
    if (!m) return `Mentee #${s.mentee}`;
    const full = `${m.first_name} ${m.last_name}`.trim();
    return full || m.email || `Mentee #${m.id}`;
}

function PendingQueue({ submissions, loading, activeId, onSelect }: Props) {
    const [search, setSearch] = useState('');

    const pending = useMemo(() => {
        const list = (submissions ?? []).filter(
            (s) => s.state !== 'finalized',
        );
        const q = search.trim().toLowerCase();
        if (!q) return list;
        return list.filter((s) => {
            const name = menteeName(s).toLowerCase();
            const mod = MODULE_LABELS[s.module].toLowerCase();
            return name.includes(q) || mod.includes(q);
        });
    }, [submissions, search]);

    if (loading) {
        return (
            <div className="pending-queue" aria-busy="true">
                <Skeleton width="100%" height="48px" borderRadius="12px" />
                <Skeleton width="100%" height="80px" borderRadius="12px" />
                <Skeleton width="100%" height="80px" borderRadius="12px" />
            </div>
        );
    }

    return (
        <div className="pending-queue">
            <input
                type="search"
                className="pending-queue__search"
                placeholder="Search mentee or module…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search pending submissions"
            />
            {pending.length === 0 ? (
                <div className="empty-state">
                    <h3>Nothing pending</h3>
                    <p>You're all caught up.</p>
                </div>
            ) : (
                <ul className="pending-queue__list">
                    {pending.map((s) => {
                        const sla = slaChip(s.submitted_at);
                        const isActive = s.id === activeId;
                        const stateChipCls =
                            s.state === 'assessed'
                                ? 'mentorship-chip--ready'
                                : 'mentorship-chip--pending';
                        const stateLabel =
                            s.state === 'assessed' ? 'AI ready' : 'Pending AI';
                        return (
                            <li key={s.id}>
                                <Card
                                    variant="row"
                                    interactive
                                    className={isActive ? 'is-active' : ''}
                                    onClick={() => onSelect(s)}
                                >
                                    <Card.Header>
                                        <Avatar name={menteeName(s)} size="md" />
                                        <div className="pending-queue__info">
                                            <p className="pending-queue__name">
                                                {menteeName(s)}
                                            </p>
                                            <div className="pending-queue__chips">
                                                <span className="mentorship-chip mentorship-chip--module">
                                                    {MODULE_LABELS[s.module]}
                                                </span>
                                                <span className={`mentorship-chip ${stateChipCls}`}>
                                                    {stateLabel}
                                                </span>
                                                <span className={`mentorship-chip ${sla.cls}`}>
                                                    {sla.label}
                                                </span>
                                            </div>
                                        </div>
                                    </Card.Header>
                                </Card>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}

export default PendingQueue;
